import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export const THEATER_PREF_KEY = 'theaterPref';

type TheaterModeValue = {
  theater: boolean;
  toggle: () => void;
  setTheater: (on: boolean) => void;
};

export const TheaterModeContext = createContext<TheaterModeValue>({
  theater: false, toggle: () => {}, setTheater: () => {},
});

const isTypingTarget = (el: EventTarget | null): boolean => {
  const n = el as HTMLElement | null;
  if (!n) return false;
  const tag = n.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || n.isContentEditable === true;
};

export const TheaterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theater, setTheaterState] = useState(false);

  const setTheater = useCallback((on: boolean) => {
    setTheaterState(on);
    try { localStorage.setItem(THEATER_PREF_KEY, on ? '1' : '0'); } catch { /* tolerate */ }
  }, []);
  const toggle = useCallback(() => setTheater(!theater), [theater, setTheater]);

  // ONE global hotkey handler: `t` toggles, Escape exits — never while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === 'Escape') { if (theater) setTheater(false); return; }
      if (e.metaKey || e.ctrlKey || e.altKey) return;   // don't intercept Cmd/Ctrl+T etc.
      if (e.key === 't' || e.key === 'T') { e.preventDefault(); setTheater(!theater); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [theater, setTheater]);

  return (
    <TheaterModeContext.Provider value={{ theater, toggle, setTheater }}>
      {children}
    </TheaterModeContext.Provider>
  );
};

export const useTheaterMode = () => useContext(TheaterModeContext);
